import { createApp, ref } from 'vue'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import App from './App.vue'
import { api, keepAlive, noteActivity, setUnauthorizedHandler, token, type Admin, type Permission } from './api'
import './styles.css'

/** The signed-in operator. A ref rather than a store — there is one of them. */
export const admin = ref<Admin | null>(null)

/**
 * Whether the signed-in operator's role holds `perm` — for HIDING what would
 * only answer 403. Never a security check: every route enforces its own.
 */
export function can(perm: Permission): boolean {
  return !!admin.value?.permissions?.includes(perm)
}

const routes: RouteRecordRaw[] = [
  { path: '/sign-in', name: 'sign-in', component: () => import('./views/SignIn.vue'), meta: { public: true } },

  // The console is organised around people. Everything a customer has is reached
  // through their own page rather than through a table of that kind of thing.
  { path: '/', name: 'home', component: () => import('./views/Home.vue') },
  { path: '/clients', name: 'customers', component: () => import('./views/Customers.vue') },
  { path: '/clients/:id', name: 'customer', component: () => import('./views/Customer.vue') },

  // Referrals (2026-09-25) are a TAB of Clients since 2026-10-14 (operator). The
  // old path redirects, so a bookmark or a link in an old note still lands.
  { path: '/referrals', redirect: { name: 'customers', query: { tab: 'referrals' } } },

  // Treasury is the one screen NOT about a person: it is about whether Tothor
  // can cover what all of them are owed.
  { path: '/treasury', name: 'treasury', component: () => import('./views/Treasury.vue') },

  // The pipeline is the money's path rather than any one account's: custody ->
  // sweep -> treasury -> swap -> card pool. It answers "where is it right now",
  // which no per-person screen can, because the money is between people.
  { path: '/pipeline', name: 'pipeline', component: () => import('./views/Pipeline.vue') },

  // Customer-to-customer transfers (2026-10-14): money moving BETWEEN people,
  // so — like Pipeline — a screen of its own rather than a tab on one person.
  { path: '/transfers', name: 'transfers', component: () => import('./views/Transfers.vue') },

  // Prospects (2026-11-01): Tothor's sales pipeline — people who are NOT
  // customers yet. Tothor's only: the menu shows it in the Tothor view, and the
  // server answers 409 in any other (the screen offers the switch).
  { path: '/prospects', name: 'prospects', component: () => import('./views/Prospects.vue'), meta: { perm: 'prospects:read' } },
  { path: '/prospects/:id', name: 'prospect', component: () => import('./views/Prospect.vue'), meta: { perm: 'prospects:read' } },

  // The operators themselves (2026-10-14): Team is SUPER_ADMIN only — the
  // `perm` hides what would only answer 403; the server checks regardless.
  // My account is every operator's own name, password and role.
  { path: '/equipe', name: 'team', component: () => import('./views/Team.vue'), meta: { perm: 'admins:write' } },
  { path: '/equipe/:id', name: 'operator', component: () => import('./views/Operator.vue'), meta: { perm: 'admins:write' } },
  { path: '/compte', name: 'account', component: () => import('./views/Account.vue') },

  { path: '/:pathMatch(.*)*', redirect: '/' },
]

const router = createRouter({ history: createWebHistory(import.meta.env.BASE_URL), routes })

router.beforeEach(async (to) => {
  if (to.meta.public) return true
  if (!token.get()) return { name: 'sign-in' }
  if (!admin.value) {
    try {
      admin.value = await api.me()
    } catch {
      return { name: 'sign-in' }
    }
  }
  if (to.meta.perm && !can(to.meta.perm as Permission)) return { name: 'home' }
  return true
})

setUnauthorizedHandler(() => {
  admin.value = null
  void router.push({ name: 'sign-in' })
})

// Keep an ACTIVE console signed in — see `keepAlive` in api/index.ts. Input
// counts as activity, so an operator reading one long page is not cut off.
for (const ev of ['pointerdown', 'keydown', 'wheel', 'touchstart']) {
  window.addEventListener(ev, noteActivity, { passive: true })
}
setInterval(() => void keepAlive(), 60_000)
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') void keepAlive()
})

createApp(App).use(router).mount('#app')
